import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { characters, getCharacterDisplayName } from "@/lib/characters";
import { useLocale } from "@/hooks/use-locale";
import { trackCTAClick } from "@/lib/analytics";

type CharacterKey = "liam" | "fabio" | "kaleb" | "clyde" | "dana" | "casper";

interface QuizOption {
  textKey: string;
  scores: Partial<Record<CharacterKey, number>>;
}

interface QuizQuestion {
  questionKey: string;
  options: QuizOption[];
}

const questions: QuizQuestion[] = [
  {
    questionKey: "quiz.q1",
    options: [
      { textKey: "quiz.q1o1", scores: { liam: 3, fabio: 1 } },
      { textKey: "quiz.q1o2", scores: { fabio: 3, kaleb: 1 } },
      { textKey: "quiz.q1o3", scores: { kaleb: 2, casper: 2 } },
      { textKey: "quiz.q1o4", scores: { dana: 3, clyde: 1 } },
    ],
  },
  {
    questionKey: "quiz.q2",
    options: [
      { textKey: "quiz.q2o1", scores: { liam: 3, clyde: 1 } },
      { textKey: "quiz.q2o2", scores: { fabio: 3, casper: 1 } },
      { textKey: "quiz.q2o3", scores: { kaleb: 3, clyde: 1 } },
      { textKey: "quiz.q2o4", scores: { dana: 3, casper: 1 } },
    ],
  },
  {
    questionKey: "quiz.q3",
    options: [
      { textKey: "quiz.q3o1", scores: { liam: 2, fabio: 2 } },
      { textKey: "quiz.q3o2", scores: { dana: 3, kaleb: 1 } },
      { textKey: "quiz.q3o3", scores: { clyde: 3, kaleb: 1 } },
      { textKey: "quiz.q3o4", scores: { casper: 3, dana: 1 } },
    ],
  },
  {
    questionKey: "quiz.q4",
    options: [
      { textKey: "quiz.q4o1", scores: { liam: 3, fabio: 1 } },
      { textKey: "quiz.q4o2", scores: { fabio: 3, dana: 1 } },
      { textKey: "quiz.q4o3", scores: { kaleb: 3, casper: 1 } },
      { textKey: "quiz.q4o4", scores: { clyde: 3, kaleb: 1 } },
    ],
  },
  {
    questionKey: "quiz.q5",
    options: [
      { textKey: "quiz.q5o1", scores: { liam: 3, fabio: 1 } },
      { textKey: "quiz.q5o2", scores: { fabio: 3, kaleb: 1 } },
      { textKey: "quiz.q5o3", scores: { kaleb: 2, casper: 2 } },
      { textKey: "quiz.q5o4", scores: { clyde: 3, dana: 1 } },
    ],
  },
  {
    questionKey: "quiz.q6",
    options: [
      { textKey: "quiz.q6o1", scores: { liam: 3, dana: 1 } },
      { textKey: "quiz.q6o2", scores: { fabio: 3, clyde: 1 } },
      { textKey: "quiz.q6o3", scores: { kaleb: 2, clyde: 2 } },
      { textKey: "quiz.q6o4", scores: { dana: 2, casper: 2 } },
    ],
  },
  {
    questionKey: "quiz.q7",
    options: [
      { textKey: "quiz.q7o1", scores: { liam: 3, casper: 1 } },
      { textKey: "quiz.q7o2", scores: { fabio: 3, kaleb: 1 } },
      { textKey: "quiz.q7o3", scores: { clyde: 3, kaleb: 1 } },
      { textKey: "quiz.q7o4", scores: { dana: 2, casper: 2 } },
    ],
  },
];

const characterSlugMap: Record<CharacterKey, string> = {
  liam: "liam-lion",
  fabio: "fabio-fox",
  kaleb: "kaleb-koala",
  clyde: "clyde-crocodile",
  dana: "dana-deer",
  casper: "casper-crab",
};

const AMAZON_STORE = "https://www.amazon.com/stores/Kalianii-MotherlyLoveandLight/page/2E97BFF2-3CBA-47CB-8B3D-3D77AF27DFD0";

const PathfinderQuiz = () => {
  const [step, setStep] = useState(-1);
  const [scores, setScores] = useState<Record<CharacterKey, number>>({
    liam: 0, fabio: 0, kaleb: 0, clyde: 0, dana: 0, casper: 0,
  });
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const { t } = useTranslation();
  const { prefix, currentLang } = useLocale();

  const handleStart = () => {
    trackCTAClick('quiz_start');
    setStep(0);
  };

  const handleSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
    const option = questions[step].options[optionIndex];

    setTimeout(() => {
      const newScores = { ...scores };
      for (const [key, value] of Object.entries(option.scores)) {
        newScores[key as CharacterKey] += value!;
      }
      setScores(newScores);
      setSelectedOption(null);

      if (step < questions.length - 1) {
        setStep(step + 1);
      } else {
        setStep(questions.length);
      }
    }, 400);
  };

  const handleRestart = () => {
    trackCTAClick('quiz_restart');
    setStep(-1);
    setScores({ liam: 0, fabio: 0, kaleb: 0, clyde: 0, dana: 0, casper: 0 });
    setSelectedOption(null);
  };

  if (step === -1) {
    return (
      <div className="text-center py-8">
        <Button onClick={handleStart} size="lg" className="rounded-full bg-primary text-primary-foreground font-bold transition-transform duration-200 hover:scale-105">
          {t("quiz.startButton")}
        </Button>
      </div>
    );
  }

  if (step >= questions.length) {
    const winner = (Object.entries(scores) as [CharacterKey, number][])
      .sort((a, b) => b[1] - a[1])[0][0];
    const slug = characterSlugMap[winner];
    const character = characters.find((c) => c.slug === slug)!;
    const displayName = getCharacterDisplayName(character, currentLang);

    return (
      <div className="max-w-lg mx-auto text-center py-8">
        <h3 className="text-2xl font-bold font-heading mb-2">{t("quiz.resultTitle")}</h3>
        <div className="my-8">
          <img src={character.image} alt={character.fullName} className="mx-auto h-48 w-auto mb-4" width={200} height={200} />
          <h4 className="text-3xl font-bold font-heading text-accent mb-2">{character.fullName}</h4>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">{character.bio}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <Button asChild className="rounded-full bg-primary text-primary-foreground font-bold transition-transform duration-200 hover:scale-105">
            <Link to={`${prefix}/${slug}`} onClick={() => trackCTAClick(`quiz_meet_${character.name}`)}>{t("quiz.meetCharacter", { name: displayName })}</Link>
          </Button>
          <Button onClick={handleRestart} variant="outline" className="rounded-full font-bold border-foreground transition-transform duration-200 hover:scale-105">
            {t("quiz.tryAgain")}
          </Button>
        </div>
        <div className="border-t border-border pt-6 mt-6">
          <p className="text-sm text-muted-foreground mb-4">{t("quiz.continueWith", { name: displayName })}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild variant="outline" size="sm" className="rounded-full font-bold transition-transform duration-200 hover:scale-105">
              <Link to={`${prefix}/activities`} onClick={() => trackCTAClick('quiz_download_cards')}>{t("quiz.downloadCards")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-full font-bold transition-transform duration-200 hover:scale-105">
              <a href={AMAZON_STORE} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('quiz_shop')}>{t("quiz.shopProducts", { name: displayName })}</a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const q = questions[step];
  const progress = ((step + 1) / questions.length) * 100;

  return (
    <div className="max-w-lg mx-auto py-8">
      <div className="mb-6">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>{t("quiz.questionOf", { current: step + 1, total: questions.length })}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <h3 className="text-xl font-bold font-heading mb-6">{t(q.questionKey)}</h3>

      <div className="space-y-3">
        {q.options.map((option, i) => (
          <button
            key={i}
            onClick={() => handleSelect(i)}
            disabled={selectedOption !== null}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all text-sm font-medium ${
              selectedOption === i
                ? "border-accent bg-accent/10 scale-[0.98]"
                : "border-border hover:border-accent/50 hover:bg-card"
            } ${selectedOption !== null && selectedOption !== i ? "opacity-50" : ""}`}
          >
            {t(option.textKey)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PathfinderQuiz;
